import DraftNameInput from "../DraftNameInput";
import styles from "../../Editor.module.css";
import type { Dispatch, SetStateAction } from "react";
import {
  BoxType,
  CanvasElement,
  ID,
  ElementError,
  VisualStyle,
} from "../../../shared/types";
import IdSelector from "../../idEditor/IdEditor";
import { isIdInvalid, getErrorsForId } from "../../utils/validationHelpers";
import FieldValidationTooltip from "../FieldValidationTooltip";
import InlineTargetEditor from "../InlineTargetEditor";

/**
 * Props for the ClassContent component.
 */
interface Props {
  classVariables: any; // Array of variable objects for the class
  setVariables: any; // Setter to update the list of variables
  className: string;
  ids: any;
  addId: (id: ID) => void;
  removeId: (id: ID) => void;
  sandbox: boolean;
  validationErrors?: ElementError[]; // Validation errors for highlighting
  elements?: any[]; // All canvas elements for ID usage tracking
  ownerElement: CanvasElement;
  visualStyle?: VisualStyle;
  pythonTutorStandalonePrimitives?: boolean;
  onCommitKind?: (kind: BoxType) => void;
  onElementsChange?: Dispatch<SetStateAction<CanvasElement[]>>;
}

/**
 * ClassContent renders editable fields for class variables (attributes).
 *
 * Each variable consists of:
 * - A `name` input box for attribute name
 * - A visual placeholder for the variable's target ID
 * - A remove button to delete the variable
 *
 * The component also provides an "Add Variable" button to append a new attribute.
 */
const ClassContent = ({
  classVariables,
  setVariables,
  className,
  ids,
  addId,
  removeId,
  sandbox,
  validationErrors,
  elements = [],
  ownerElement,
  visualStyle = "memoryviz",
  pythonTutorStandalonePrimitives = false,
  onCommitKind,
  onElementsChange,
}: Props) => {
  const useInlineTargetEditor =
    visualStyle === "pythonTutor" && !pythonTutorStandalonePrimitives;

  // Add a new empty variable to the list
  const addVariable = () =>
    setVariables([...classVariables, { name: "", targetId: "_" }]);

  // Remove a variable at a given index
  const removeVariable = (i: number) =>
    setVariables(classVariables.filter((_: any, idx: any) => idx !== i));

  // Update the name of a variable at a given index
  const changeName = (i: number, val: string) => {
    const next = classVariables.map((item: any, idx: number) => idx === i ? { ...item, name: val } : item);
    setVariables(next);
    onCommitKind?.({ name: "class", type: "class", value: null, className, classVariables: next });
  };

  // Update the targetId of a variable at a given index
  const setTargetId = (i: number, id: ID) =>
    {
      const nextVariables = classVariables.map((v: any, idx: any) =>
        idx === i ? { ...v, targetId: id } : v
      );
      setVariables(nextVariables);
      onCommitKind?.({
        name: "class",
        type: "class",
        value: null,
        className,
        classVariables: nextVariables,
      });
    };

  return (
    <div className={styles.contentContainer}>
      {classVariables.length > 0 && (
        <div className={styles.ItemContainer}>
          {classVariables.map((v: any, idx: any) => {
            const hasError = isIdInvalid(validationErrors, v.targetId);
            const fieldErrors = getErrorsForId(validationErrors, v.targetId);
            return (
              <div key={idx} className={styles.pairItem}>
                <DraftNameInput
                  placeholder="variable"
                  value={v.name}
                  onCommit={name => changeName(idx, name)}
                  className={styles.variableNameBox}
                />
                <div className={styles.idSelectButtonWrapper}>
                  {useInlineTargetEditor ? (
                    <InlineTargetEditor
                      ownerElement={ownerElement}
                      currentTarget={v.targetId}
                      onTargetChange={(id) => setTargetId(idx, id as ID)}
                      addId={addId}
                      elements={elements}
                      onElementsChange={onElementsChange}
                      validationErrors={fieldErrors}
                    />
                  ) : (
                    <FieldValidationTooltip errors={fieldErrors}>
                      <IdSelector
                        currentId={v.targetId}
                        ids={ids}
                        onAdd={addId}
                        onSelect={(id) => setTargetId(idx, id)}
                        onRemove={removeId}
                        buttonClassName={`${styles.collectionIdBox} ${
                          hasError ? styles.errorId : ""
                        }`}
                        sandbox={sandbox}
                        editable={true}
                        elements={elements}
                      />
                    </FieldValidationTooltip>
                  )}
                </div>
                <button
                  onClick={() => removeVariable(idx)}
                  className={styles.deleteVariableButton}
                  title="Delete variable"
                  aria-label="Delete variable"
                />
              </div>
            );
          })}
        </div>
      )}
      <div>
        <button className={styles.addButton} onClick={addVariable}>
          Add Variable
        </button>
      </div>
    </div>
  );
};

export default ClassContent;
